import { Injectable, inject, signal, computed } from '@angular/core';
import { groupBy, times, concat } from 'lodash-es';
import dayjs from 'dayjs';
import { map, catchError, of } from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';

import { EventService, LocCDType, EventCalendarDetail } from 'api';

export interface CalendarDay {
  date: string;
  dateStr: string;
  day: number;
  dayOfWeek: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
  x: 'past' | 'current' | 'next';
}

export interface CalendarEvent {
  date: string;
  time: string;
  ShowTime?: string;
  ShowName?: string;
  eventCode?: string;
  eventName?: string;
  eventDateTime?: string;
  eventStatusCode?: string;
  filterType: 'SHOW' | 'COMP' | 'OCCA';
  displayShowName?: boolean;
  url?: string;
  type?: string;
  isShow?: boolean;
}

export interface CalendarOptions {
  includePastDates?: boolean;
  includeNextMonthDates?: boolean;
  defaultLocation?: LocCDType;
  showCode?: string;
  groupByWeeks?: boolean;
}

export interface CalendarRequest {
  month: number;
  year: number;
  showCode: string;
  locationCD?: LocCDType;
  options?: CalendarOptions;
}

@Injectable({
  providedIn: 'root',
})
export class SharedCalendarService {
  private readonly eventService = inject(EventService);
  private readonly requestSignal = signal<CalendarRequest | null>(null);

  private readonly calendarResource = rxResource({
    params: () => this.requestSignal(),
    stream: ({ params: request }) => {
      if (!request) {
        return of([] as CalendarDay[][]);
      }

      const defaultOptions: CalendarOptions = {
        includePastDates: true,
        includeNextMonthDates: false,
        defaultLocation: 'STRASBURG_PA',
        groupByWeeks: true,
        ...request.options,
      };

      const location = request.locationCD || defaultOptions.defaultLocation!;
      const { startRange, endRange } = this.getDateRange(
        request.month,
        request.year
      );

      return this.eventService
        .getEventCalendar('OT', location, undefined, startRange, endRange)
        .pipe(
          map((showsResponse) => {
            const shows = this.reshapeShowsResponse(
              showsResponse,
              location,
              request.showCode
            );

            const groupedEvents = groupBy(shows, 'date');

            const days = this.generateCalendarDays(
              request.month,
              request.year,
              groupedEvents,
              defaultOptions
            );

            // Always return grouped by weeks for consistency
            return this.groupDaysIntoWeeks(days);
          }),
          catchError((error) => {
            console.error('Error fetching calendar data:', error);
            throw error;
          })
        );
    },
  });

  readonly calendarData = computed(() => this.calendarResource.value() ?? []);
  readonly isLoading = computed(() => this.calendarResource.isLoading());
  readonly error = computed(() => this.calendarResource.error());

  loadCalendarData(request: CalendarRequest) {
    this.requestSignal.set(request);
  }

  private getDateRange(month: number, year: number) {
    const startRange = dayjs()
      .year(year)
      .month(month)
      .startOf('month')
      .startOf('day')
      .toDate();

    const endRange = dayjs()
      .year(year)
      .month(month)
      .endOf('month')
      .endOf('day')
      .toDate();

    return { startRange, endRange };
  }

  private reshapeShowsResponse(
    shows: EventCalendarDetail,
    locationCD: LocCDType,
    showCode?: string
  ): CalendarEvent[] {
    let currentEventCD: string;
    let currentEventDate: string;

    let result = Object.values(shows.eventMap).flatMap((show: any) => {
      return show;
    });

    let newArray: CalendarEvent[] = [...result];

    if (showCode) {
      newArray = newArray.filter((single) => single.eventCode === showCode);
    }

    return newArray.map((single) => {
      const eventDateTime = single.eventDateTime || '';
      single.date = dayjs(eventDateTime).format('YYYY-MM-DD');
      single.ShowTime = dayjs(eventDateTime).format('h:mm');
      single.ShowName = single.eventCode?.startsWith('BTS')
        ? 'BTST'
        : single.eventName;
      single.filterType = 'SHOW';
      single.displayShowName = true;
      single.time = dayjs(eventDateTime).format('h:mm A');

      if (
        single.isShow &&
        currentEventCD === single.eventCode &&
        currentEventDate === single.date
      ) {
        single.displayShowName = false;
      }

      currentEventCD = single.eventCode || '';
      currentEventDate = single.date;

      return single;
    });
  }

  private generateCalendarDays(
    month: number,
    year: number,
    groupedEvents: Record<string, CalendarEvent[]>,
    options: CalendarOptions
  ): CalendarDay[] {
    const dates = this.getDateArray(month, year);
    const result = this.mergeEventsWithDateArr(groupedEvents, dates);

    return result;
  }

  private getDateArray(month: number, year: number): CalendarDay[] {
    let m = dayjs().year(year).month(month);
    const dim = m.daysInMonth();
    const dates: CalendarDay[] = [];
    const today = dayjs();

    m = m.startOf('month').startOf('day');

    times(dim, (n) => {
      const dateStr = m.format('YYYY-MM-DD');
      const isToday = m.isSame(today, 'day');

      dates.push({
        date: dateStr,
        dateStr: dateStr,
        dayOfWeek: m.format('dddd'),
        day: m.date(),
        isCurrentMonth: true,
        isToday: isToday,
        events: [],
        x: 'current',
      });

      m = m.add(1, 'day');
    });

    const prevMonthDates: CalendarDay[] = [];
    let prevM = dayjs().year(year).month(month).startOf('month').startOf('day');

    times(prevM.day(), (n) => {
      prevM = prevM.subtract(1, 'day');
      const dateStr = prevM.format('YYYY-MM-DD');

      prevMonthDates.push({
        date: dateStr,
        dateStr: dateStr,
        dayOfWeek: prevM.format('dddd'),
        day: prevM.date(),
        isCurrentMonth: false,
        isToday: false,
        events: [],
        x: 'past',
      });
    });

    const nextMonthDates: CalendarDay[] = [];
    let nextM = dayjs().year(year).month(month).endOf('month').startOf('day');

    times(6 - nextM.day(), (n) => {
      nextM = nextM.add(1, 'day');
      const dateStr = nextM.format('YYYY-MM-DD');

      nextMonthDates.push({
        date: dateStr,
        dateStr: dateStr,
        dayOfWeek: nextM.format('dddd'),
        day: nextM.date(),
        isCurrentMonth: false,
        isToday: false,
        events: [],
        x: 'next',
      });
    });

    prevMonthDates.reverse();
    const finalDates = concat(prevMonthDates, dates, nextMonthDates);
    return finalDates;
  }

  private mergeEventsWithDateArr(
    groupedEvents: Record<string, CalendarEvent[]>,
    dates: CalendarDay[]
  ): CalendarDay[] {
    dates.forEach((date) => {
      date.events = groupedEvents[date.dateStr] || [];
    });
    return dates;
  }

  private groupDaysIntoWeeks(days: CalendarDay[]): CalendarDay[][] {
    const weeks: CalendarDay[][] = [];
    const daysCopy = [...days];
    while (daysCopy.length) {
      weeks.push(daysCopy.splice(0, 7));
    }
    return weeks;
  }
}
